import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import queryString from 'query-string';
import { getSimManagementErrorFromAxiosError } from '.';
import { Company } from '../models/company';
import { QueryResult } from '../models/queryResult';
import { IDashboardData } from '../components/graphicalDashboard/GraphicalDashboard';

import config from '../config';
const log = getLogger('simManagement.api.companyApi');

interface CompanySearchOptions {
  take: number;
  skip: number;
  searchTerm: string;
  wrapWith: {
    totalCount: boolean;
  };
}

interface QueryOptions {
  skip: number;
  take: number;
  id?: number;
  nameContains?: string;
  wrapWith?: string; // "count" | "total-count";
  orderBy?: string;
}

export function getById(id: number): Promise<Company> {
  const url = config.API_BASE_URL + `api/company/${id}`;
  return new Promise<Company>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as Company;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load company ${id}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function get(options: Partial<CompanySearchOptions>): Promise<QueryResult<Company>> {
  const resolvedOptions = Object.assign(defaultSearchOptions, options);
  const queryOptions: QueryOptions = {
    orderBy: 'name',
    skip: resolvedOptions.skip,
    take: resolvedOptions.take,
  };

  if (resolvedOptions.searchTerm) {
    queryOptions.nameContains = resolvedOptions.searchTerm;
  }

  const wrapWithItems = [];
  wrapWithItems.push('count');
  if (resolvedOptions.wrapWith.totalCount) {
    wrapWithItems.push('total-count');
  }

  if (wrapWithItems.length > 0) {
    queryOptions.wrapWith = wrapWithItems.join(',');
  }

  const queryParams = queryString.stringify(queryOptions);
  const url = config.API_BASE_URL + `api/company?${queryParams}`;
  return new Promise<QueryResult<Company>>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<QueryResult<Company>>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load company list.', simManagementError);
        reject(simManagementError);
      });
  });
}

export function getDashboardDataForCompany(companyId: number) {
  const baseUrl = config.API_BASE_URL + `api/company/${companyId}/dashboard`;

  return new Promise<IDashboardData>((resolve, reject) => {
    axios
      .get(baseUrl)
      .then((response: AxiosResponse<IDashboardData>) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load company dashboard ${companyId}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getWebookUrl(companyId: number): Promise<string | undefined> {
  const url = config.API_BASE_URL + `api/company/${companyId}/webhook-url`;
  return new Promise<string | undefined>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as string;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load company ${companyId}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

const defaultSearchOptions: CompanySearchOptions = {
  searchTerm: '',
  skip: 0,
  take: 50,
  wrapWith: {
    totalCount: false,
  },
};
